import { useEffect } from 'react';

import { ENERGY_EFFICIENCY_CLASSES, ENERGY_SOURCES, HEATING_TYPES, HOT_WATER_SUPPLY_TYPES } from 'cpool-constants';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { optionValues, optionValuesWithLast } from './optionValues';
import type { StepProps } from './types';
import { FormTextInput } from '../../../shared/inputs/FormTextInput';
import { FormCheckboxGroup, FormRadioGroup } from '../FormControls';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema';

export const HeatingHotWaterStep: React.FC<StepProps> = ({ disabled, realEstateIndex }) => {
    const { t } = useTranslation();
    const { control, getValues, setValue } = useFormContext<SchemaRealEstatesForm>();
    const prefix = `immobilien.${realEstateIndex}.baubeschreibung.heizungWarmwasser` as const;
    const heatingTypes = useWatch({ control, name: `${prefix}.heizungsart` }) ?? [];
    const energySources = useWatch({ control, name: `${prefix}.energietraeger` }) ?? [];
    const hasOtherHeatingType = heatingTypes.includes(HEATING_TYPES.ANDERE);
    const hasOtherEnergySource = energySources.includes(ENERGY_SOURCES.ANDERE);

    useEffect(() => {
        if (!hasOtherHeatingType && getValues(`${prefix}.heizungsartAndere`) != null) {
            setValue(`${prefix}.heizungsartAndere`, null, { shouldDirty: true, shouldValidate: true });
        }
        if (!hasOtherEnergySource && getValues(`${prefix}.energietraegerAndere`) != null) {
            setValue(`${prefix}.energietraegerAndere`, null, { shouldDirty: true, shouldValidate: true });
        }
    }, [getValues, hasOtherEnergySource, hasOtherHeatingType, prefix, setValue]);
    return (
        <div className="space-y-3">
            <FormCheckboxGroup
                name={`${prefix}.heizungsart`}
                label={t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.HEATING_TYPE')}
                options={optionValuesWithLast(
                    HEATING_TYPES,
                    HEATING_TYPES.ANDERE,
                    t,
                    'REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.HEATING_TYPE',
                )}
                disabled={disabled}
                extraContent={
                    hasOtherHeatingType ? (
                        <FormTextInput
                            control={control}
                            id={`${prefix}.heizungsartAndere`}
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.OTHER_HEATING_TYPE')}
                            disabled={disabled}
                        />
                    ) : null
                }
            />
            <FormCheckboxGroup
                name={`${prefix}.energietraeger`}
                label={t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.ENERGY_SOURCE')}
                options={optionValuesWithLast(
                    ENERGY_SOURCES,
                    ENERGY_SOURCES.ANDERE,
                    t,
                    'REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.ENERGY_SOURCE',
                )}
                disabled={disabled}
                extraContent={
                    hasOtherEnergySource ? (
                        <FormTextInput
                            control={control}
                            id={`${prefix}.energietraegerAndere`}
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.OTHER_ENERGY_SOURCE')}
                            disabled={disabled}
                        />
                    ) : null
                }
            />
            <FormRadioGroup
                name={`${prefix}.warmwasserversorgung`}
                label={t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.HOT_WATER')}
                options={optionValues(HOT_WATER_SUPPLY_TYPES, t, 'REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.HOT_WATER')}
                disabled={disabled}
            />
            <FormRadioGroup
                name={`${prefix}.energieeffizienz`}
                label={t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.ENERGY_EFFICIENCY')}
                options={optionValues(
                    ENERGY_EFFICIENCY_CLASSES,
                    t,
                    'REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.ENERGY_EFFICIENCY',
                )}
                disabled={disabled}
            />
        </div>
    );
};
